import { useMemo, useState } from 'react';

import { ChartTypeEnum } from '../../../../../model/chart_type.enum';
import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceAccounts,
} from '../../../../../providers/portfolio-performance';
import {
  buildIndexedComparison,
  ComparisonSeriesInput,
  formatComparisonDate,
} from '../../../../../providers/portfolio-performance/build-indexed-comparison';
import { isReferenceStrategyAccount } from '../../../../../providers/portfolio-performance/reference-strategies/reference-strategy.constants';
import { useThemeMode } from '../../../../../providers/theme-provider';
import { getLineChartColors } from '../../../../../theme/colors';
import RadioButtonList from '../../../../../components/radio-button-list';
import { RangeListEnum } from '../../../types';
import { EXTENDED_RANGES, ExtendedRange } from '../../../range';
import { PortfolioPerformanceChartType } from '../../portfolio-perfomance-chart-type';

interface IndexedComparisonTabProps {
  // The rows the user ticked for comparison in the Overview table above
  // (real strategies AND Buy-and-Hold references, treated identically).
  // Empty => nothing ticked yet; the tab shows a select-rows prompt.
  accounts: IPortfolioPerformanceAccounts[];
  reportsByAccount: Record<string, IPortfolioPerformanceAccountReport>;
  isNamesBlurred?: boolean;
}

// Opens on 3M by default — long enough to be a meaningful comparison, short
// enough that a young account usually has data across it.
const DEFAULT_RANGE: ExtendedRange = RangeListEnum.M3;

export const IndexedComparisonTab = ({
  accounts,
  reportsByAccount,
  isNamesBlurred = false,
}: IndexedComparisonTabProps) => {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const [rangeSelected, setRangeSelected] = useState<ExtendedRange>(DEFAULT_RANGE);

  const palette = getLineChartColors(resolvedMode);

  // One series per selected account, coloured by palette position so a given
  // account keeps its colour as the window changes.
  const seriesInputs = useMemo<ComparisonSeriesInput[]>(
    () =>
      accounts.map((account, i) => ({
        key: account.name,
        label: account.name,
        color: palette[i % palette.length],
        historical: reportsByAccount[account.name]?.historical,
      })),
    [accounts, reportsByAccount, palette],
  );

  const built = useMemo(
    () => buildIndexedComparison(seriesInputs, rangeSelected),
    [seriesInputs, rangeSelected],
  );

  // Only series with at least one point in the window can be drawn. Series
  // with no overlapping history are kept in the legend (greyed, "no data in
  // range") rather than silently vanishing. Memoised so extraSeries/windowStart
  // below keep a stable identity across unrelated parent renders — otherwise the
  // chart's overlay effect refits (destroying pan/zoom) on every re-render.
  const drawable = useMemo(() => built.filter((s) => s.data.length > 0), [built]);
  const hasChart = drawable.length > 0;

  // Earliest rebase day across drawn series == the common window start. A
  // series rebasing later than this started mid-window — flagged in the legend.
  const windowStart = useMemo(() => {
    const starts = drawable
      .map((s) => s.rebasedAt)
      .filter((t): t is NonNullable<typeof t> => t !== null);
    return starts.length > 0 ? Math.min(...(starts as number[])) : null;
  }, [drawable]);

  const main = drawable[0];
  const extraSeries = useMemo(
    () => drawable.slice(1).map((s) => ({ data: s.data, color: s.color })),
    [drawable],
  );

  const labelOf = (key: string) =>
    isNamesBlurred && !isReferenceStrategyAccount(key) ? '••••••' : key;

  // Closing cumulative % for the legend — the series' last plotted value.
  const closeDelta = (v: number): string => {
    const r = Number(v.toFixed(2));
    const val = Object.is(r, -0) ? 0 : r;
    return `${val > 0 ? '+' : ''}${val.toFixed(2)}%`;
  };

  return (
    <div className="indexed-comparison-tab">
      <div className="comparison-controls">
        <div className="comparison-subtitle">
          Each series rebased to 0% at the start of the selected window, compounded by its own daily
          returns.
        </div>
        <RadioButtonList
          options={EXTENDED_RANGES}
          defaultValue={rangeSelected}
          dividerBefore={['MTD']}
          onChange={(range) => setRangeSelected(range as ExtendedRange)}
        />
      </div>

      <div className="comparison-chart-area">
        {hasChart ? (
          <PortfolioPerformanceChartType
            key={rangeSelected}
            type={ChartTypeEnum.Line}
            data={main.data}
            color={main.color}
            extraSeries={extraSeries}
            percent
            zeroLine
            precision={2}
            lineWidth={2}
            viewportResetKey={rangeSelected}
            showTooltip
            gridColor={isDark ? '#1f3645' : 'rgba(9,45,58,0.08)'}
          />
        ) : (
          <div className="no-data-metrics">
            <span>
              {accounts.length === 0
                ? 'Select rows in the table above to compare.'
                : 'Select one or more accounts with daily-return history to compare.'}
            </span>
          </div>
        )}
      </div>

      <div className="comparison-legend">
        {built.map((s) => {
          const empty = s.data.length === 0;
          const startsLate =
            !empty && windowStart !== null && s.rebasedAt !== null && s.rebasedAt > windowStart;
          return (
            <div key={s.key} className={`legend-item${empty ? ' legend-item-empty' : ''}`}>
              <span className="legend-swatch" style={{ backgroundColor: s.color }} />
              <span className="legend-label">{labelOf(s.key)}</span>
              {!empty && (
                <span className="legend-close" style={{ color: s.color }}>
                  ({closeDelta(s.data[s.data.length - 1].value)})
                </span>
              )}
              {empty && <span className="legend-note">no data in range</span>}
              {startsLate && s.rebasedAt !== null && (
                <span className="legend-note">from {formatComparisonDate(s.rebasedAt)}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
